import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../../backend/firebase";
import Dataset from "../../components/Dataset"; 
import { FaSearch, FaDatabase, FaLayerGroup, FaHashtag, FaArrowRight, FaBoxOpen } from "react-icons/fa";
import { motion, AnimatePresence } from "motion/react";
import "./Search.css";

const Search = () => {
  const [searchType, setSearchType] = useState("DATASETS");
  const [searchText, setSearchText] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async () => {
    if (!searchText.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setHasSearched(true);
    setResults([]);

    try {
      const term = searchText.trim();

      if (searchType === "DATASETS") {
        const keywords = term.split(" ").filter((k) => k).slice(0, 10);
        const q = query(
          collection(db, "datasets"),
          where("searchQuery", "array-contains-any", keywords),
          limit(20)
        );
        const snapshot = await getDocs(q);
        setResults(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'dataset' })));
      } 
      else if (searchType === "SETA_ID") {
        const q = query(collection(db, "datasets"), where("__name__", "==", term));
        const snapshot = await getDocs(q);
        setResults(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'dataset' })));
      } 
      else if (searchType === "COLLECTIONS") {
        const q = query(
          collection(db, "collections"),
          where("collectionName", ">=", term),
          where("collectionName", "<=", term + '\uf8ff'),
          limit(20)
        );
        const snapshot = await getDocs(q);
        setResults(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), type: 'collection' })));
      }
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchText.trim()) handleSearch();
      else { setResults([]); setHasSearched(false); }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchText, searchType]);

  const searchModes = [
    { id: "DATASETS", label: "Datasets", icon: <FaDatabase /> },
    { id: "COLLECTIONS", label: "Collections", icon: <FaLayerGroup /> },
    { id: "SETA_ID", label: "Seta ID", icon: <FaHashtag /> },
  ];

  return (
    <div className="search-page-container">
      {/* LEFT SIDEBAR */}
      <aside className="search-sidebar">
        <div className="sidebar-header">
          <h2>Explorer</h2>
          <p>Search the Seta Registry</p>
        </div>

        <div className="search-control-group">
          <div className="input-wrapper">
            <input
              autoFocus
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Start typing..."
              className="search-input"
            />
            {loading && <div className="spinner-loader"></div>}
          </div>

          <div className="mode-list">
            <label>SEARCH MODE</label>
            {searchModes.map((mode) => (
              <button
                key={mode.id}
                onClick={() => { setSearchType(mode.id); setResults([]); }}
                className={`mode-item ${searchType === mode.id ? 'active' : ''}`}
              >
                <span className="mode-icon">{mode.icon}</span>
                <span className="mode-label">{mode.label}</span>
                {searchType === mode.id && <motion.div layoutId="active-pill" className="active-indicator" />}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* MAIN CONTENT AREA */}
      <main className="search-main-content">
        <header className="results-topbar">
          <div className="topbar-left">
            <h1>{hasSearched ? "Search Results" : "Discover"}</h1>
            <span className="meta-badge">
              {hasSearched ? `${results.length} Matches` : "Ready"}
            </span>
          </div>
        </header>

        <div className="results-scroll-area">
          {loading && !results.length ? (
            <div className="status-container">Searching...</div>
          ) : results.length > 0 ? (
            <motion.div 
              className="results-grid"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <AnimatePresence>
                {results.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="grid-item"
                  >
                    {searchType === "COLLECTIONS" ? (
                      <div className="collection-card" onClick={() => navigate(`/collections/${item.id}`)}>
                        <div className="card-top">
                          <div className="col-icon-bg">
                            {item.collectionName?.[0]?.toUpperCase() || "C"}
                          </div>
                          <span className="privacy-tag">Public</span>
                        </div>
                        <div className="card-mid">
                          <h3>{item.collectionName}</h3>
                          <p>{item.description || "No description provided."}</p>
                        </div>
                        <div className="card-bot">
                          <span>View Collection</span>
                          <FaArrowRight />
                        </div>
                      </div>
                    ) : (
                      <div className="dataset-wrapper-fix">
                        <Dataset datasetid={item.id} />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          ) : hasSearched ? (
            <div className="status-container">
              <FaBoxOpen className="status-icon" />
              <h3>No results found</h3>
              <p>Try adjusting your keywords or switching search modes.</p>
            </div>
          ) : (
            <div className="status-container idle">
              <div className="idle-visual"></div>
              <p>Select a mode to begin searching</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Search;